import { useMemo } from 'react'
import { useEventLinkedPlans } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import {
  resolveEventActivityCastTargets,
  resolveEventActivityTimelineActors,
  resolveEventActivityTimelineAssignments,
  ResolvedTimelineActor,
  ResolvedAssignmentParticipant,
  ResolvedAssignmentEntity,
  toResolvedParticipants,
} from '../components/eventActivityTimelineResolver'
import {
  buildLinkedPlanAssignments,
  LinkedPlanAssignment,
  resolveLinkedPlanEncounterAssignmentRows,
  resolveLinkedPlanPhaseDividers,
} from './resolveLinkedPlanEncounterAssignmentRows'
import {
  EventPlanOverridesByPlanId,
  getActorOverrideCharacterId,
  getActorOverrideIgnored,
  getActorOverrideReplacementActor,
  getEncounterAssignmentAbilityHidden,
  getPlanActorOverrides,
  getPlanCastTargetOverrides,
  getPlanEncounterAssignmentOverrides,
} from '../components/eventActivityPlanOverrides'
import { getEventActivityUnresolvedIssues } from './applyEventActivityPlanOverrides'
import { EventActivityParticipantEntry } from '../components/eventActivityParticipantEntry'
import { collectionRoleMatcher } from '../../TeamPlans/components/assignmentEntityCounting'

type LinkedPlanResolverResult = ReturnType<
  typeof resolveEventActivityTimelineAssignments
>
type LinkedPlanActorResolverResult = ReturnType<
  typeof resolveEventActivityTimelineActors
>
type LinkedPlanCastTargetResolverResult = ReturnType<
  typeof resolveEventActivityCastTargets
>

export type EventActivityResolvedAssignmentAbility = {
  abilityKey: string
  spellId: number
  name: string
}

export type EventActivityLinkedPlanAssignment = LinkedPlanAssignment

export type EventActivityLinkedRaidPlanData = {
  linkedRaidPlan: RaidPlan
  phaseDividers: { name: string; at: number }[]
  linkedPlanAssignments: EventActivityLinkedPlanAssignment[]
  selectedRows: EncounterAbilityRow[]
  resolvedAssignmentAbilitiesByCharacterId: Record<
    number,
    EventActivityResolvedAssignmentAbility[]
  >
  resolvedActors: ResolvedTimelineActor[]
  resolverResult: LinkedPlanResolverResult
  actorResolverResult: LinkedPlanActorResolverResult
  castTargetResolverResult: LinkedPlanCastTargetResolverResult
  linkedPlanIssueCount: number
  hasLinkedPlanIssues: boolean
}

export type EventActivityLinkedPlanEntry =
  | { type: 'raid'; data: EventActivityLinkedRaidPlanData }
  | { type: 'visual'; data: VisualPlan }

export type EventActivityLinkedPlanData = {
  linkedPlans: EventActivityLinkedPlanEntry[]
  raidPlans: EventActivityLinkedRaidPlanData[]
  linkedVisualPlans: VisualPlan[]
  selectedParticipants: ResolvedAssignmentParticipant[]
  linkedPlanIssueCount: number
  hasLinkedPlanIssues: boolean
}

export const indexResolvedAssignmentAbilityKeysByCharacterId = ({
  resolvedByScopeBoxKey,
  selectedParticipants,
}: {
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>
  selectedParticipants: ResolvedAssignmentParticipant[]
}) => {
  const resolvedAbilityKeysByCharacterId: Record<number, Set<string>> = {}
  const addAbility = (characterId: number, abilityKey: string) => {
    if (!resolvedAbilityKeysByCharacterId[characterId]) {
      resolvedAbilityKeysByCharacterId[characterId] = new Set<string>()
    }
    resolvedAbilityKeysByCharacterId[characterId].add(abilityKey)
  }
  Object.entries(resolvedByScopeBoxKey).forEach(([scopeBoxKey, entities]) => {
    const abilityKey = scopeBoxKey.split(':')[0]
    if (!abilityKey) return
    entities.forEach((entity) => {
      if (entity.kind === 'participant') {
        addAbility(entity.participant.characterId, abilityKey)
        return
      }
      if (entity.kind !== 'aggregate') return
      const matcher = collectionRoleMatcher(entity.sourceEntity)
      if (!matcher) return
      selectedParticipants.forEach((participant) => {
        if (matcher(participant.role)) {
          addAbility(participant.characterId, abilityKey)
        }
      })
    })
  })
  return resolvedAbilityKeysByCharacterId
}

const resolveLinkedRaidPlan = ({
  activity,
  linkedRaidPlan,
  allEntries,
  selectedParticipants,
}: {
  activity: wowaudit.EventActivity
  linkedRaidPlan: RaidPlan
  allEntries: EventActivityParticipantEntry[]
  selectedParticipants: ResolvedAssignmentParticipant[]
}): EventActivityLinkedRaidPlanData => {
  const encounterRows = resolveLinkedPlanEncounterAssignmentRows({
    raidPlan: linkedRaidPlan,
    timeline: linkedRaidPlan.timeline,
    unknownAbilityLabel: 'Unknown ability',
  })
  const phaseDividers = resolveLinkedPlanPhaseDividers({
    raidPlan: linkedRaidPlan,
    timeline: linkedRaidPlan.timeline,
  })
  const linkedPlanAssignments = buildLinkedPlanAssignments({
    encounterAssignments: linkedRaidPlan.timeline.encounter_assignments,
    encounterRows,
  })
  const selectedRows = linkedPlanAssignments.map((entry) => entry.row)
  const encounterAssignmentOverrides = getPlanEncounterAssignmentOverrides(
    activity.planOverrides as EventPlanOverridesByPlanId,
    linkedRaidPlan.id,
  )
  const resolverResult = resolveEventActivityTimelineAssignments({
    activityId: activity.id,
    entries: allEntries,
    encounterRows: selectedRows,
    abilities: linkedPlanAssignments.map((entry) => entry.ability),
    spreadRoleCollections: false,
    encounterAssignmentOverrides,
    overrideParticipants: selectedParticipants,
  })
  const actorOverrides = getPlanActorOverrides(
    activity.planOverrides as EventPlanOverridesByPlanId,
    linkedRaidPlan.id,
  )
  const sourceActors = linkedRaidPlan.timeline.actors ?? []
  const ignoredActorUuids: string[] = []
  const pinnedCharacterIdByActorUuid: Record<string, number> = {}
  const effectiveActors = sourceActors.map((actor) => {
    const replacementActor = getActorOverrideReplacementActor({
      actorOverrides,
      actorUuid: actor.uuid,
    })
    const effectiveActor = replacementActor ?? actor
    if (getActorOverrideIgnored({ actorOverrides, actorUuid: actor.uuid })) {
      ignoredActorUuids.push(effectiveActor.uuid)
    }
    const overrideCharacterId = getActorOverrideCharacterId({
      actorOverrides,
      actorUuid: actor.uuid,
    })
    const replacementPreferredCharacterId =
      replacementActor?.preferred_character_id
    const pinnedCharacterId =
      typeof overrideCharacterId === 'number'
        ? overrideCharacterId
        : replacementPreferredCharacterId
    if (typeof pinnedCharacterId === 'number') {
      pinnedCharacterIdByActorUuid[effectiveActor.uuid] = pinnedCharacterId
    }
    return effectiveActor
  })
  const ignoredActorUuidSet = new Set(ignoredActorUuids)
  const actorResolverResult = resolveEventActivityTimelineActors({
    activityId: activity.id,
    entries: allEntries,
    actors: effectiveActors,
    pinnedCharacterIdByActorUuid,
    ignoredActorUuids,
  })
  const resolvedActors = actorResolverResult.resolvedActors.map(
    (resolvedActor) => ({
      ...resolvedActor,
      isIgnored: ignoredActorUuidSet.has(resolvedActor.actor.uuid),
    }),
  )
  const visibleResolvedActors = resolvedActors.filter(
    (resolvedActor) =>
      resolvedActor.actor.casts.some((cast) => !cast.hidden) ||
      Boolean(
        getActorOverrideReplacementActor({
          actorOverrides,
          actorUuid: resolvedActor.actor.uuid,
        }),
      ),
  )
  const actorIssues = visibleResolvedActors
    .filter(
      (resolvedActor) =>
        !resolvedActor.participant && resolvedActor.isIgnored !== true,
    )
    .map((resolvedActor) => {
      if (resolvedActor.issue) return resolvedActor.issue
      const available =
        resolvedActor.actor.spec_id !== null
          ? selectedParticipants.filter(
              (participant) =>
                participant.specId === resolvedActor.actor.spec_id,
            ).length
          : selectedParticipants.filter(
              (participant) =>
                participant.classId === resolvedActor.actor.class_id,
            ).length
      return {
        type: 'unmatched_actor' as const,
        actorLabel: resolvedActor.actor.label,
        requested: 1,
        available,
        missing: 1,
      }
    })
  const actorResolverResultWithOverrides = {
    ...actorResolverResult,
    resolvedActors: visibleResolvedActors,
    issues: actorIssues,
  }
  const castTargetOverrides = getPlanCastTargetOverrides(
    activity.planOverrides as EventPlanOverridesByPlanId,
    linkedRaidPlan.id,
  )
  const castTargetResolverResult = resolveEventActivityCastTargets({
    activityId: activity.id,
    entries: allEntries,
    actors: visibleResolvedActors.map((resolvedActor) => resolvedActor.actor),
    generalCasts: linkedRaidPlan.timeline.general?.casts ?? [],
    castTargetOverrides,
  })
  const { resolvedByScopeBoxKey } = resolverResult
  const hiddenAbilityKeys = linkedPlanAssignments
    .filter((assignment) =>
      getEncounterAssignmentAbilityHidden({
        encounterAssignments: encounterAssignmentOverrides,
        abilityKey: assignment.abilityKey,
      }),
    )
    .map((assignment) => assignment.abilityKey)
  const unresolvedIssues = getEventActivityUnresolvedIssues(
    resolvedByScopeBoxKey,
    hiddenAbilityKeys,
  )
  const resolverResultWithOverrides = {
    ...resolverResult,
    resolvedByScopeBoxKey,
    issues: unresolvedIssues,
  }
  const rowByAbilityKey = selectedRows.reduce<
    Record<string, EncounterAbilityRow>
  >((acc, row) => {
    acc[row.abilityKey] = row
    return acc
  }, {})
  const resolvedAbilityKeysByCharacterId =
    indexResolvedAssignmentAbilityKeysByCharacterId({
      resolvedByScopeBoxKey,
      selectedParticipants,
    })
  const resolvedAssignmentAbilitiesByCharacterId = Object.entries(
    resolvedAbilityKeysByCharacterId,
  ).reduce<Record<number, EventActivityResolvedAssignmentAbility[]>>(
    (acc, [characterIdString, abilityKeys]) => {
      const characterId = Number(characterIdString)
      acc[characterId] = Array.from(abilityKeys)
        .map((abilityKey) => {
          const row = rowByAbilityKey[abilityKey]
          if (!row) return null
          return {
            abilityKey,
            spellId: row.spellId,
            name: row.name,
          }
        })
        .filter(
          (ability): ability is EventActivityResolvedAssignmentAbility =>
            ability !== null,
        )
        .sort((left, right) => {
          if (left.name !== right.name) {
            return left.name.localeCompare(right.name)
          }
          return left.abilityKey.localeCompare(right.abilityKey)
        })
      return acc
    },
    {},
  )
  const linkedPlanIssueCount =
    unresolvedIssues.length +
    actorResolverResultWithOverrides.issues.length +
    castTargetResolverResult.issues.length

  return {
    linkedRaidPlan,
    phaseDividers,
    linkedPlanAssignments,
    selectedRows,
    resolvedAssignmentAbilitiesByCharacterId,
    resolvedActors: actorResolverResultWithOverrides.resolvedActors,
    resolverResult: resolverResultWithOverrides,
    actorResolverResult: actorResolverResultWithOverrides,
    castTargetResolverResult,
    linkedPlanIssueCount,
    hasLinkedPlanIssues: linkedPlanIssueCount > 0,
  }
}

type UseEventActivityLinkedPlanDataOptions = {
  activities: wowaudit.EventActivity[]
  enabled?: boolean
}

const useEventActivityLinkedPlanData = ({
  activities,
  enabled = true,
}: UseEventActivityLinkedPlanDataOptions) => {
  const { team } = useTeam()
  const { allEntries, event } = useTeamEventParticipantsContext()
  const linkedPlanRefs = useMemo(
    () =>
      Array.from(
        new Set(
          activities.flatMap((activity) =>
            (activity.linkedPlans ?? []).map(
              (entry) => `${entry.type}:${entry.id}`,
            ),
          ),
        ),
      ).sort(),
    [activities],
  )
  const { data: linkedPlansResponse, isLoading: isRaidPlansLoading } =
    useEventLinkedPlans(team.id, event.id, linkedPlanRefs, enabled)
  const raidPlans = linkedPlansResponse?.raidPlans
  const visualPlans = linkedPlansResponse?.visualPlans
  const visualPlansById = useMemo(
    () => new Map((visualPlans ?? []).map((plan) => [plan.id, plan])),
    [visualPlans],
  )

  const linkedPlanDataByActivityId = useMemo(() => {
    const map = new Map<number, EventActivityLinkedPlanData>()
    const plansById = new Map((raidPlans ?? []).map((plan) => [plan.id, plan]))

    activities.forEach((activity) => {
      const selectedParticipants = toResolvedParticipants(
        allEntries,
        activity.id,
      )
      const linkedPlans = (
        activity.linkedPlans ?? []
      ).flatMap<EventActivityLinkedPlanEntry>((entry) => {
        if (entry.type === 'raid') {
          const linkedRaidPlan = plansById.get(entry.id)
          if (!linkedRaidPlan) return []
          return [
            {
              type: 'raid',
              data: resolveLinkedRaidPlan({
                activity,
                linkedRaidPlan,
                allEntries,
                selectedParticipants,
              }),
            },
          ]
        }
        const visualPlan = visualPlansById.get(entry.id)
        return visualPlan ? [{ type: 'visual', data: visualPlan }] : []
      })
      const resolvedRaidPlans = linkedPlans.flatMap((entry) =>
        entry.type === 'raid' ? [entry.data] : [],
      )
      const linkedVisualPlans = linkedPlans.flatMap((entry) =>
        entry.type === 'visual' ? [entry.data] : [],
      )
      const linkedPlanIssueCount = resolvedRaidPlans.reduce(
        (sum, planData) => sum + planData.linkedPlanIssueCount,
        0,
      )

      map.set(activity.id, {
        linkedPlans,
        raidPlans: resolvedRaidPlans,
        linkedVisualPlans,
        selectedParticipants,
        linkedPlanIssueCount,
        hasLinkedPlanIssues: linkedPlanIssueCount > 0,
      })
    })

    return map
  }, [activities, allEntries, raidPlans, visualPlansById])

  return {
    linkedPlanDataByActivityId,
    isRaidPlansLoading,
    visualPlansById,
  }
}

export default useEventActivityLinkedPlanData
